import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

const TURNSTILE_SCRIPT_ID = 'tapevault-turnstile-script';
const TURNSTILE_SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string) || '0x4AAAAAAFPjCrQ-E-GPvg-X';

function friendlyError(message: string): string {
  const m = message.toLowerCase();
  if (m.includes('captcha') || m.includes('human verification')) {
    return '请完成人机验证后再登录，验证过期时请重新勾选。';
  }
  if (m.includes('invalid login credentials') || m.includes('invalid email or password')) {
    return '用户名或密码错误。';
  }
  if (m.includes('banned') || m.includes('not allowed')) {
    return '此账号已停用，请联系管理员。';
  }
  return message;
}

function TurnstileWidget({
  resetNonce,
  onToken,
  onError,
}: {
  resetNonce: number;
  onToken: (token: string) => void;
  onError: (message: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | undefined>(undefined);
  const tokenCallbackRef = useRef(onToken);
  const errorCallbackRef = useRef(onError);

  useEffect(() => { tokenCallbackRef.current = onToken; }, [onToken]);
  useEffect(() => { errorCallbackRef.current = onError; }, [onError]);

  useEffect(() => {
    let disposed = false;
    const renderWidget = () => {
      if (disposed || !containerRef.current || !window.turnstile) return;
      containerRef.current.replaceChildren();
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        action: 'login',
        callback: (token) => tokenCallbackRef.current(token),
        'expired-callback': () => {
          tokenCallbackRef.current('');
          errorCallbackRef.current('人机验证已过期，请重新验证。');
        },
        'error-callback': () => {
          tokenCallbackRef.current('');
          errorCallbackRef.current('人机验证加载失败，请刷新页面重试。');
        },
      });
    };

    const existing = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
    if (window.turnstile) {
      renderWidget();
    } else {
      const script = existing || document.createElement('script');
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.addEventListener('load', renderWidget);
      if (!existing) document.head.appendChild(script);
    }

    return () => {
      disposed = true;
      if (widgetIdRef.current && window.turnstile?.remove) window.turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = undefined;
    };
  }, []);

  useEffect(() => {
    if (resetNonce > 0 && widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
      tokenCallbackRef.current('');
    }
  }, [resetNonce]);

  return <div ref={containerRef} className="min-h-[65px] flex justify-center" aria-label="Human verification" />;
}

export default function Login() {
  const { signIn, user, loading } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaReset, setCaptchaReset] = useState(0);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate('/', { replace: true });
  }, [loading, user, navigate]);

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-slate-100 px-6 text-slate-700">
        <div className="flex items-center gap-3 text-sm" role="status" aria-live="polite">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" />
          <span>正在加载登录服务…</span>
        </div>
      </main>
    );
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setError('');
    if (!username.trim() || !password) {
      setError('请输入用户名和密码。');
      return;
    }
    if (!captchaToken) {
      setError('请先完成人机验证。');
      return;
    }
    setBusy(true);
    try {
      const result = await signIn(username, password, captchaToken);
      if (result.error) {
        setError(friendlyError(result.error));
        setCaptchaToken('');
        setCaptchaReset((value) => value + 1);
        return;
      }
      navigate('/', { replace: true });
    } catch {
      setError('无法连接登录服务，请稍后重试。');
      setCaptchaToken('');
      setCaptchaReset((value) => value + 1);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-slate-100 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-7 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-slate-900 shadow-sm">
            <img src="/tape-icon.png" alt="" className="h-11 w-11 object-contain" />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-950">TapeVault</h1>
          <p className="mt-2 text-sm text-slate-500">Backup tape inventory</p>
        </div>

        <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="mb-6"><h2 className="text-lg font-semibold text-slate-900">登录</h2><p className="mt-1 text-sm text-slate-500">使用 TapeVault 账号继续。</p></div>
          <form onSubmit={submit} className="space-y-4">
            <div><label htmlFor="username" className="block text-sm font-medium text-slate-700 mb-1.5">用户名</label><input id="username" name="username" type="text" autoComplete="username" value={username} onChange={(event) => { setUsername(event.target.value); setError(''); }} placeholder="用户名" className="h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10" /></div>
            <div><label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">密码</label><div className="relative"><input id="password" name="password" type={showPw ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={(event) => { setPassword(event.target.value); setError(''); }} placeholder="密码" className="h-11 w-full rounded-md border border-slate-300 bg-white px-3 pr-16 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10" /><button type="button" onClick={() => setShowPw((value) => !value)} className="absolute right-2 top-1/2 h-8 -translate-y-1/2 px-2 text-xs font-medium text-slate-500 hover:text-slate-900" aria-label={showPw ? '隐藏密码' : '显示密码'}>{showPw ? '隐藏' : '显示'}</button></div></div>
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3"><div className="mb-2 text-center text-xs font-medium text-slate-600">人机验证</div><TurnstileWidget resetNonce={captchaReset} onToken={(token) => { setCaptchaToken(token); if (token) setError(''); }} onError={setError} /></div>
            {error && <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">{error}</div>}
            <button type="submit" disabled={busy} className="inline-flex h-11 w-full items-center justify-center rounded-md bg-slate-900 px-4 text-sm font-medium text-white transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900/30 disabled:cursor-not-allowed disabled:opacity-50">{busy ? '正在登录…' : '登录'}</button>
          </form>
        </section>
        <p className="mt-6 text-center text-xs text-slate-400">TapeVault · Secure backup media records</p>
      </div>
    </main>
  );
}
